import { CA } from 'src/app/shared/entities/ca';
import { CaType } from 'src/app/shared/entities/ca-type';
import { CaRequestDto } from 'src/app/shared/services/api/dto/ca-request-dto';
import { UpdateCaRequestDto } from 'src/app/shared/services/api/dto/update-ca-request-dto';
import { Lang } from 'src/app/shared/services/lang/lang.types';

export const SIGNED_BY_EXTERNAL = 'External'; // "Magic" value

export enum CaFormField {
  profileName = 'profileName',
  tokenName = 'tokenName',
  signKey = 'signKey',
  signingAlgo = 'signingAlgo',
  signedBy = 'signedBy',
  validityUnits = 'validityUnits',
  validityDays = 'validityDays',
  validityYears = 'validityYears',
  expirationDate = 'expirationDate',
  defaultCRLDistPoint = 'defaultCRLDistPoint',
  defaultOCSPServiceLocator = 'defaultOCSPServiceLocator',
  useLdapDnOrder = 'useLdapDnOrder',
  subjectDn = 'subjectDn',
  certChain = 'certChain',
}

export enum ValidityUnit {
  day = 'day',
  year = 'year',
}

export interface CaFormValue {
  [CaFormField.subjectDn]: string | null;
  [CaFormField.profileName]: string | null;
  [CaFormField.tokenName]: string | null;
  [CaFormField.signKey]: string | null;
  [CaFormField.signingAlgo]: string | null;
  [CaFormField.signedBy]: string | null;
  [CaFormField.validityUnits]: string | null;
  [CaFormField.validityDays]: string | null;
  [CaFormField.validityYears]: string | null;
  [CaFormField.expirationDate]: Date | null;
  [CaFormField.defaultCRLDistPoint]: string | null;
  [CaFormField.defaultOCSPServiceLocator]: string | null;
  [CaFormField.useLdapDnOrder]: boolean | null;
  [CaFormField.certChain]: string | null;
}

const TODAY = (() => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
})();

function expirationDateForDays(validityDays: number): Date {
  const expirationDate = new Date(TODAY);
  expirationDate.setDate(expirationDate.getDate() + validityDays);
  return expirationDate;
}

function expirationDateForYears(validityYears: number): Date {
  const expirationDate = new Date(TODAY);
  expirationDate.setFullYear(expirationDate.getFullYear() + validityYears);
  return expirationDate;
}

const validityToDateConverters: Record<
  ValidityUnit,
  (validity: number) => Date
> = {
  [ValidityUnit.day]: validity => expirationDateForDays(validity),
  [ValidityUnit.year]: validity => expirationDateForYears(validity),
};

export function validityToExpirationDate(
  validityUnit: ValidityUnit,
  validity: number,
): Date {
  return validityToDateConverters[validityUnit](validity);
}

const msPerDay = 1000 * 60 * 60 * 24;

export function expirationDateToValidityDays(expirationDate: Date): number {
  return Math.round((expirationDate.getTime() - TODAY.getTime()) / msPerDay);
}

// The function assumes the form is valid
export function caFormValueToDto(
  formValue: Partial<CaFormValue>,
  selfSignedOption: string,
): CaRequestDto {
  if (!formValue[CaFormField.subjectDn]) {
    throw new Error('This should not happen: empty subnectDn');
  }
  if (!formValue[CaFormField.tokenName]) {
    throw new Error('This should not happen: empty tokenName');
  }
  if (!formValue[CaFormField.signedBy]) {
    throw new Error('This should not happen: empty signedBy');
  }
  if (!formValue[CaFormField.signKey]) {
    throw new Error('This should not happen: empty signKey');
  }
  if (!formValue[CaFormField.signingAlgo]) {
    throw new Error('This should not happen: empty signAlgo');
  }
  if (!formValue[CaFormField.expirationDate]) {
    throw new Error('This should not happen: empty signAlgo');
  }
  return {
    dn: formValue[CaFormField.subjectDn],
    profileName: formValue[CaFormField.profileName] || null,
    tokenName: formValue[CaFormField.tokenName],
    signKey: formValue[CaFormField.signKey],
    signingAlgo: formValue[CaFormField.signingAlgo],
    signedBy:
      formValue[CaFormField.signedBy] === selfSignedOption
        ? null
        : formValue[CaFormField.signedBy],
    validityDays: expirationDateToValidityDays(
      formValue[CaFormField.expirationDate],
    ),
    defaultCRLDistPoint: formValue[CaFormField.defaultCRLDistPoint] || null,
    defaultOCSPServiceLocator:
      formValue[CaFormField.defaultOCSPServiceLocator] || null,
    useLdapDnOrder: formValue[CaFormField.useLdapDnOrder] || null,
  };
}

interface SubjectDn {
  commonName?: string;
  email?: string;
  orgName?: string;
  orgUnits: string[];
  organization?: string;
  locality?: string;
  state?: string;
  country?: string;
}

// // https://docs.pingidentity.com/pingauthorize/10.2/pingauthorize_server_administration_guide/paz_cert_subject_dns.html
// const SUBJECT_DN_ENCODING: Record<string, keyof SubjectDn> = {
//   CN: 'commonName',
//   E: 'email',
//   O: 'orgName',
//   OU: 'orgUnits',
//   L: 'locality',
//   ST: 'state',
//   C: 'country',
// };
//
// function parseSubjectDn(subjectDn: string): SubjectDn {
//   const parsedSubjectDn: SubjectDn = { orgUnits: [] };
//   const attributes = subjectDn.split(',').map(keyvalue => keyvalue.split('='));
//   attributes.forEach(([key, value]) => {
//     if (key in SUBJECT_DN_ENCODING) {
//       const attributeName = SUBJECT_DN_ENCODING[key];
//       if (attributeName !== 'orgUnits') {
//         parsedSubjectDn[attributeName] = value;
//       } else {
//         parsedSubjectDn[attributeName].push(value);
//       }
//     }
//   });
//   return parsedSubjectDn;
// }

const MS_IN_DAY = 1000 * 60 * 60 * 24;

function expireTimeToDays(expireTime: number): number {
  return Math.round((expireTime - Date.now()) / MS_IN_DAY);
}

export function caToFormValue(ca: CA, lang: Lang): CaFormValue {
  return {
    [CaFormField.subjectDn]: ca.subjectDn ?? null,
    [CaFormField.profileName]: null, // used for creation and not stored on BE
    [CaFormField.tokenName]: ca.token ?? null,
    [CaFormField.signKey]: ca.signKey ?? null,
    [CaFormField.signingAlgo]: ca.signingAlgo ?? null,
    [CaFormField.signedBy]:
      ca.caType === CaType.ROOT
        ? lang.features.calist.create.selfSignedOption
        : ca.caType === CaType.SIGNED_BY_EXTERNAL
          ? lang.features.calist.create.externallySignedOption
          : (ca.signedBy ?? ''),
    [CaFormField.validityUnits]:
      lang.features.calist.create.inputs.validityUnits.options.find(
        ({ value }) => value === ValidityUnit.day,
      )?.label ?? null,
    [CaFormField.validityDays]: ca.expireTime
      ? expireTimeToDays(ca.expireTime).toString()
      : null,
    [CaFormField.validityYears]: null,
    [CaFormField.expirationDate]: ca.expireTime
      ? new Date(ca.expireTime)
      : null,
    [CaFormField.defaultCRLDistPoint]: ca.defaultCRLDistPoint ?? null,
    [CaFormField.defaultOCSPServiceLocator]:
      ca.defaultOCSPServiceLocator ?? null,
    [CaFormField.useLdapDnOrder]: ca.useLdapDNOrder ?? null,
    [CaFormField.certChain]: '',
  };
}

export function formValueToUpdateDto(
  formValue: Partial<CaFormValue>,
): UpdateCaRequestDto {
  return {
    defaultCRLDistPoint: formValue[CaFormField.defaultCRLDistPoint] || null,
    defaultOCSPServiceLocator:
      formValue[CaFormField.defaultOCSPServiceLocator] || null,
    useLdapDNOrder: !!formValue[CaFormField.useLdapDnOrder],
  };
}
